import React, { useRef, useState } from 'react';
import { Card, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import infoIco from 'assets/images/i-circle.svg';
import UserHttp from '../services/profile.service';

interface UPIFormProps {
  customer: any;
  user: any;
  setCustomer: (customer: any) => void;
}

// ✅ Secure Regex
const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
const nameRegex = /^[A-Za-z ]{2,50}$/; // alphabets + spaces only, between 2–50 chars

const UPIForm: React.FC<UPIFormProps> = ({ customer, user, setCustomer }) => {
  const refUpi = useRef<any>(null);
  const [verification, setVerification] = useState<
    'idle' | 'verifying' | 'success' | 'failed'
  >('idle');

  const handleVerify = async (values: any, setErrors: any) => {
    const trimmedName = values.nameAsOnUPI.trim();
    const trimmedUpi = values.upi.trim();

    if (!trimmedUpi) {
      setErrors({ upi: 'UPI handle is required' });
      return;
    }
    if (!trimmedName) {
      setErrors({ nameAsOnUPI: 'Name is required' });
      return;
    }

    setVerification('verifying');
    try {
      const res: any = await UserHttp.upiVerify({
        transactionType: 'VERIFICATION',
        transactionMethod: 'UPI',
        referenceId: customer.customerId,
        referenceType: 'CUSTOMER',
        customerAccount: {
          accountType: 'BANK',
          name: customer.bankDetails.nameAsOnBankAcount,
          accountNo: customer.bankDetails.bankAccount,
          bank: customer.bankDetails.bankName,
          ifsc: customer.bankDetails.ifsc,
        },
        upiAccount: {
          name: trimmedName,
          upi: trimmedUpi,
          accountType: 'UPI',
        },
      });

      if (res.data) {
        const updatedCustomer = await UserHttp.getProfile(
          customer.customerId,
          user?.userId
        );
        setCustomer(updatedCustomer.data.data.customer);

        if (updatedCustomer.data.data.customer.upiDetails.state === 'REJECTED') {
          setErrors({ upi: res.data.data.message });
          setVerification('failed');
          toast.error('Failed to update UPI details');
        } else {
          setVerification('success');
          toast.success('UPI details submitted for verification.');
        }
      } else {
        setVerification('failed');
      }
    } catch (e: any) {
      setErrors({ upi: 'Invalid UPI handle' });
      setVerification('failed');
      toast.error('Invalid UPI handle');
    }
  };

  const isDisabled = (state: any) =>
    verification === 'success' || state === 'PENDING' || state === 'ACCEPTED';

  return (
    <Card className="mt-5">
      <Card.Body className="p-5 pb-0">
        <Card.Title>UPI Details</Card.Title>

        <Formik
          innerRef={refUpi}
          initialValues={{
            nameAsOnUPI: customer?.upiDetails?.name || '',
            upi: customer?.upiDetails?.accountNo || '',
          }}
          validationSchema={Yup.object().shape({
            nameAsOnUPI: Yup.string()
              .trim()
              .matches(nameRegex, 'Enter a valid name (only alphabets and spaces)')
              .required('Name is required'),
            upi: Yup.string()
              .trim()
              .matches(upiRegex, 'Enter a valid UPI ID (e.g., name@bank)')
              .required('UPI handle is required'),
          })}
          onSubmit={(values, { setErrors }) => handleVerify(values, setErrors)}
        >
          {({ values, errors, touched, setFieldValue, setErrors }) => (
            <Form>
              <Row>
                <Col md={6}>
                  <div className="form-group mb-2">
                    <label className="form-label">Name As per UPI</label>
                    <Field
                      type="text"
                      name="nameAsOnUPI"
                      className="form-control"
                      disabled={isDisabled(customer?.upiDetails?.state)}
                      onChange={(e: any) =>
                        setFieldValue('nameAsOnUPI', e.target.value.trimStart())
                      }
                    />
                    {errors.nameAsOnUPI &&
                      touched.nameAsOnUPI &&
                      typeof errors.nameAsOnUPI === 'string' && (
                        <small className="error-text">
                          <img src={infoIco} alt="" /> {errors.nameAsOnUPI}
                        </small>
                      )}
                  </div>
                </Col>

                <Col md={6}>
                  <div className="form-group mb-2">
                    <label className="form-label">UPI Handle</label>
                    <Field
                      type="text"
                      name="upi"
                      className="form-control"
                      value={values.upi}
                      disabled={isDisabled(customer?.upiDetails?.state)}
                      onChange={(e: any) =>
                        setFieldValue('upi', e.target.value.trim())
                      }
                    />
                    {errors.upi &&
                      touched.upi &&
                      typeof errors.upi === 'string' && (
                        <small className="error-text">
                          <img src={infoIco} alt="" /> {errors.upi}
                        </small>
                      )}
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-end py-4">
                {(
                  !customer?.upiDetails ||
                  Object.keys(customer?.upiDetails || {}).length === 0 ||
                  customer?.upiDetails?.state === 'REJECTED'
                ) && verification !== 'success' && (
                  <Button
                    type="submit"
                    disabled={verification === 'verifying'}
                    variant="primary"
                  >
                    {verification === 'verifying' ? (
                      <>
                        Verifying... <Spinner animation="border" size="sm" />
                      </>
                    ) : (
                      'Verify and Save'
                    )}
                  </Button>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default UPIForm;
